"use client";

import { useMemo } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { SiteContentByLocale } from "@/lib/content/pages/site";
import { FaInstagram, FaLinkedinIn, FaWhatsapp } from "react-icons/fa6";

function normalizeLocale(pathname: string | null): "fr" | "en" {
  if (!pathname) return "fr";
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg === "en" ? "en" : "fr";
}

export default function Footer({ content }: { content?: SiteContentByLocale }) {
  const pathname = usePathname();
  const locale = useMemo(() => normalizeLocale(pathname), [pathname]);
  const t = (path: string) => `/${locale}${path}`;

  const f = content?.[locale]?.footer;
  const year = new Date().getFullYear();

  const navFallback =
    locale === "fr"
      ? ["Adhésion", "Services", "Événements", "Destinations", "À propos"]
      : ["Membership", "Services", "Events", "Destinations", "About"];
  const navLabels = f
    ? [f.navMembership, f.navServices, f.navEvents, f.navDestinations, f.navAbout]
    : navFallback;
  const navLinks = [
    { label: navLabels[0], href: t("/membership/private") },
    { label: navLabels[1], href: t("/services/lifestyle") },
    { label: navLabels[2], href: t("/events") },
    { label: navLabels[3], href: t("/destinations") },
    { label: navLabels[4], href: t("/about") },
  ];

  const copy = f
    ? { terms: f.terms, privacy: f.privacy, rights: f.rights.replace("{year}", String(year)) }
    : locale === "fr"
      ? {
          terms: "Conditions générales",
          privacy: "Politique de confidentialité",
          rights: `© ${year} Welconcept. Tous droits réservés.`,
        }
      : {
          terms: "Terms and conditions",
          privacy: "Privacy Policy",
          rights: `© ${year} Welconcept. All Rights Reserved.`,
        };

  const email = f?.email ?? "contact@welconcept.com";
  const addressLines = (
    f?.address ??
    "Boutique Immeuble Concorde,\nen face de l'échangeur — Carré 29-30\nTokpa Hoho, Cotonou"
  ).split("\n");

  return (
    <footer
      className="relative overflow-hidden text-white/90"
      style={{ background: "#0b0b0d" }}
    >
      <div className="relative z-10 mx-auto max-w-[1400px] px-6 pt-20 pb-[clamp(7rem,14vw,12rem)]">
        {/* Main grid — left info / centre CTA / right nav */}
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          {/* Left — socials, email, address */}
          <div className="flex flex-col gap-7">
            <div className="flex items-center gap-5 text-white/70">
              <a
                href="https://www.instagram.com/welconcept.conciergerie/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="transition hover:text-white"
              >
                <FaInstagram className="h-6 w-6" />
              </a>
              <a
                href="https://www.linkedin.com/company/welconcept/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="transition hover:text-white"
              >
                <FaLinkedinIn className="h-6 w-6" />
              </a>
              <a
                href="https://wa.me/33614830298"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="transition hover:text-white"
              >
                <FaWhatsapp className="h-6 w-6" />
              </a>
            </div>

            <a
              href={`mailto:${email}`}
              className="font-body text-[20px] font-light text-white transition hover:text-white/60"
            >
              {email}
            </a>

            <address className="font-body text-[15px] not-italic leading-relaxed text-white/55">
              {addressLines.map((line, i) => (
                <span key={i}>
                  {i > 0 && <br />}
                  {line}
                </span>
              ))}
            </address>
          </div>

          {/* Right — nav links */}
          <nav className="flex flex-col items-start gap-4 lg:items-end">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="font-body text-[13px] font-light uppercase tracking-[0.12em] text-white/80 transition hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Legal row — pinned to the very bottom */}
      <div className="relative z-20 mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-3 px-6 pb-6 font-body text-[14px] text-white/45 sm:absolute sm:inset-x-0 sm:bottom-0 sm:flex-row">
        <Link href={t("/terms")} className="underline underline-offset-4 transition hover:text-white">
          {copy.terms}
        </Link>
        <p className="order-first sm:order-none">{copy.rights}</p>
        <Link href={t("/privacy")} className="underline underline-offset-4 transition hover:text-white">
          {copy.privacy}
        </Link>
      </div>

      {/* Giant background wordmark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-0 flex select-none justify-center overflow-hidden leading-none"
      >
        <span
          className="font-heading translate-y-[26%] whitespace-nowrap text-center font-semibold lowercase tracking-tight"
          style={{ fontSize: "clamp(6rem, 17vw, 18rem)", color: "rgba(255,255,255,0.06)" }}
        >
          welconcept
        </span>
      </div>
    </footer>
  );
}
